import { FC, ReactNode } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { adminColors } from '../adminTheme';

interface Props {
  label: ReactNode;
  /** Right-aligned value, already formatted and bidi-isolated by the caller. */
  value: ReactNode;
  /** 0..1. Values above 1 are clamped for the bar but not for the caption. */
  ratio: number | null;
  color: string;
  /** Small caption under the bar (limit source, warn threshold, a note). */
  caption?: ReactNode;
  /** Right of the value — usually a status chip. */
  adornment?: ReactNode;
}

/**
 * A labelled proportion bar.
 *
 * Extracted from the queue meter in `AdminDashboardPage`, which uses exactly
 * this treatment (6px track, fully rounded, 0.7s width transition, and a 6%
 * minimum fill so a tiny non-zero value is still visible rather than
 * indistinguishable from zero).
 *
 * Used for the by-service / by-feature breakdowns.
 * Those breakdowns deliberately use sorted bars rather than a pie: seven
 * services spanning $381 to $0.02 are unreadable as arc slices, and a sorted
 * list with exact figures beside each is what someone actually reads. It also
 * needs no chart library and flips correctly in RTL for free.
 *
 * `ratio: null` renders the track with no fill — "no limit", not "zero".
 */
export const UsageMeter: FC<Props> = observer(
  ({ label, value, ratio, color, caption, adornment }) => {
    const theme = useTheme();
    const c = adminColors(theme);
    const pct = ratio === null ? null : Math.min(100, Math.max(0, ratio * 100));

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.625 }}>
          <Typography sx={{ flex: 1, fontSize: 12.5, color: c.ink, minWidth: 0 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: c.ink }}>{value}</Typography>
          {adornment}
        </Box>
        <Box
          sx={{
            height: 6,
            backgroundColor: theme.palette.grey[300],
            borderRadius: 999,
            overflow: 'hidden',
          }}
        >
          {pct !== null && (
            <Box
              sx={{
                height: '100%',
                // 6% floor: a 0.2% share should still be visible as "some", not
                // render as an empty track indistinguishable from nothing.
                width: `${Math.max(6, pct)}%`,
                backgroundColor: color,
                borderRadius: 999,
                transition: 'width .7s',
              }}
            />
          )}
        </Box>
        {caption && (
          <Typography sx={{ fontSize: 11, color: c.faint, mt: 0.5 }}>{caption}</Typography>
        )}
      </Box>
    );
  },
);
